import React from 'react';
import { Mountain, ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <Mountain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Roam</h1>
                <p className="text-sm text-gray-600">Outdoor Adventure Apps</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                Home
              </Link>
              <Link to="/#apps" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                Our Apps
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link
          to="/"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 sm:p-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-xl">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Privacy Policy</h2>
          </div>

          <p className="text-gray-500 text-sm mb-8">
            Effective Date: March 13, 2026
          </p>

          <div className="space-y-6">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Introduction</h3>
              <p className="text-gray-600 leading-relaxed">
                BareBones Dev, LLC ("we," "us," or "our") operates the Roam family of apps,
                including Roam Weather, Roam Office Tracker, and other apps we may release in the
                future (collectively, "our apps" or "Roam apps"). This Privacy Policy explains how
                we collect, use, and protect your information when you use any of our apps. By
                using our apps, you agree to the practices described in this policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Information We Collect</h3>
              <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
                <li>
                  <strong>Account Information</strong> — When you create an account, we collect your
                  email address and password. Authentication is handled by Firebase Auth; passwords
                  are never stored by us in plain text.
                </li>
                <li>
                  <strong>App Data</strong> — Data you enter or save within our apps (such as
                  locations, preferences, office check-ins, and schedules) is stored in Google Cloud
                  Firestore so it syncs across your devices.
                </li>
                <li>
                  <strong>Local Device Storage</strong> — We may store settings and cached data
                  locally on your device to improve performance and enable offline access.
                </li>
                <li>
                  <strong>Usage &amp; Analytics Data</strong> — We use Firebase Analytics to collect
                  anonymous usage data such as feature usage frequency, session duration, and crash
                  reports. This data helps us improve our apps.
                </li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">How We Use Your Information</h3>
              <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
                <li>Provide the core features of each app (e.g., weather forecasts, office day tracking).</li>
                <li>Save and sync your data and preferences across devices.</li>
                <li>Improve our apps through aggregated, anonymous analytics.</li>
                <li>Process in-app purchases and manage your subscription.</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Third-Party Services</h3>
              <p className="text-gray-600 leading-relaxed mb-3">
                We rely on the following third-party services to operate our apps. Not all services
                are used by every app. Each service may collect data as described in their own
                privacy policies:
              </p>
              <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
                <li>
                  <strong>Firebase</strong> (Authentication, Cloud Firestore, Analytics) —{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Google Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>NOAA</strong> (weather data API) —{' '}
                  <a
                    href="https://www.noaa.gov/protecting-your-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    NOAA Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>RevenueCat</strong> (in-app purchases) —{' '}
                  <a
                    href="https://www.revenuecat.com/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    RevenueCat Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>Microsoft Azure</strong> (hosting) —{' '}
                  <a
                    href="https://privacy.microsoft.com/privacystatement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    Microsoft Privacy Policy
                  </a>
                </li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Data Sharing</h3>
              <p className="text-gray-600 leading-relaxed">
                We do not sell your personal data. We do not display third-party advertisements.
                Your data is shared only with the third-party services listed above as necessary to
                operate our apps and provide their features.
              </p>
            </section>

            {/* Data Retention & Deletion */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Data Retention &amp; Deletion</h3>
              <p className="text-gray-600 leading-relaxed">
                We retain your data for as long as your account is active. You may request deletion
                of your account and all associated data at any time by contacting us through
                our{' '}
                <Link
                  to="/support"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Support page
                </Link>
                . Upon receiving a deletion request, we will remove your account and associated
                Firestore data.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Children's Privacy</h3>
              <p className="text-gray-600 leading-relaxed">
                Our apps are not directed at children under the age of 13. We do not knowingly
                collect personal information from children under 13. If you believe we have
                inadvertently collected such information, please contact us through
                our{' '}
                <Link
                  to="/support"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Support page
                </Link>{' '}
                and we will promptly delete it.
              </p>
            </section>

            {/* Security */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Security</h3>
              <p className="text-gray-600 leading-relaxed">
                We take reasonable measures to protect your information from unauthorized access,
                alteration, or destruction. However, no method of electronic transmission or storage
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Changes to This Policy</h3>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. When we do, we will revise the
                "Effective Date" at the top of this page. Your continued use of our apps after
                any changes constitutes your acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Us</h3>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy, please reach
                out through our{' '}
                <Link
                  to="/support"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Support page
                </Link>
                .
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};
